import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import type {
  InventoryItem,
  Rect,
  Slot,
  Warehouse,
  Zone,
} from "./warehouse-types";

const ZONE_PALETTE = [
  "#2dd4bf",
  "#60a5fa",
  "#f472b6",
  "#fbbf24",
  "#a78bfa",
  "#34d399",
];
const SLOT_PALETTE = ["#38bdf8", "#818cf8", "#f59e0b", "#4ade80", "#fb7185"];

const ZONE_PER_ROW = 3;
const ZONE_W = 240;
const ZONE_H = 180;
const ZONE_GAP = 40;
const ZONE_START = 60;

function defaultZoneRect(index: number): Rect {
  const col = index % ZONE_PER_ROW;
  const row = Math.floor(index / ZONE_PER_ROW);
  return {
    x: ZONE_START + col * (ZONE_W + ZONE_GAP),
    y: ZONE_START + row * (ZONE_H + ZONE_GAP),
    width: ZONE_W,
    height: ZONE_H,
  };
}

function defaultSlotRect(zoneRect: Rect, index: number): Rect {
  const perRow = 2;
  const w = 90;
  const h = 50;
  const gap = 12;
  const col = index % perRow;
  const row = Math.floor(index / perRow);
  return {
    x: zoneRect.x + 12 + col * (w + gap),
    y: zoneRect.y + 34 + row * (h + gap),
    width: w,
    height: h,
  };
}

// Build canvas zones/slots from the source-of-truth warehouseArea/storageLocation
// records, pulling visual data (rect/color) from the saved layoutConfig where ids
// match and a default layout for records that have none yet. Slot items come from
// the live productInventory records keyed by storageLocationId.
export function buildZonesFromDb(
  dbAreas: any[],
  layoutZones: Zone[],
  itemsByLocationId: Map<string, InventoryItem[]>,
): Zone[] {
  const layoutZoneById = new Map(layoutZones.map((z) => [z.id, z]));

  return (dbAreas || []).map((area, areaIndex) => {
    const lz = layoutZoneById.get(area.id);
    const zoneRect = lz?.rect ?? defaultZoneRect(areaIndex);
    const layoutSlotById = new Map((lz?.slots ?? []).map((s) => [s.id, s]));
    const dbLocs: any[] = area.storageLocation ?? [];

    const slots: Slot[] = dbLocs.map((loc, locIndex) => {
      const ls = layoutSlotById.get(loc.id);
      return {
        id: loc.id,
        name: loc.name ?? "",
        rect: ls?.rect ?? defaultSlotRect(zoneRect, locIndex),
        color: ls?.color ?? SLOT_PALETTE[locIndex % SLOT_PALETTE.length],
        items: itemsByLocationId.get(loc.id) ?? [],
      };
    });

    return {
      id: area.id,
      name: area.name ?? "",
      rect: zoneRect,
      color: lz?.color ?? ZONE_PALETTE[areaIndex % ZONE_PALETTE.length],
      slots,
    };
  });
}

// Reconcile the real warehouseArea/storageLocation records to match the canvas
// (create new, update renamed, delete removed) and return a copy of the canvas
// with client ids replaced by the server-generated ids, so layoutConfig stays
// in sync and re-saving stays idempotent.
export async function syncWarehouseEntities(
  warehouseId: string,
  canvas: Warehouse,
): Promise<Warehouse> {
  const dbAreas = (await orpcClient.warehouseArea.list({
    filter: { warehouseId },
  })) as any[];
  const dbAreaById = new Map((dbAreas || []).map((a) => [a.id, a]));
  const canvasZoneIds = new Set(canvas.zones.map((z) => z.id));

  for (const area of dbAreas || []) {
    if (!canvasZoneIds.has(area.id)) {
      await single(orpcClient.warehouseArea.delete([{ id: area.id }]));
    }
  }

  const syncedZones: Zone[] = [];

  for (const zone of canvas.zones) {
    let areaId = zone.id;
    const dbArea = dbAreaById.get(zone.id);

    if (dbArea) {
      await single(
        orpcClient.warehouseArea.update([{ id: zone.id, name: zone.name }])
      );
    } else {
      const created = (await single(
        orpcClient.warehouseArea.create([
          {
            warehouseId,
            name: zone.name,
            enable: true,
          },
        ])
      )) as any;
      areaId = created.id;
    }

    const dbLocs: any[] = dbArea?.storageLocation ?? [];
    const dbLocById = new Map(dbLocs.map((l) => [l.id, l]));
    const canvasSlotIds = new Set(zone.slots.map((s) => s.id));

    for (const loc of dbLocs) {
      if (!canvasSlotIds.has(loc.id)) {
        await single(orpcClient.storageLocation.delete([{ id: loc.id }]));
      }
    }

    const syncedSlots: Slot[] = [];

    for (const slot of zone.slots) {
      let slotId = slot.id;

      if (dbLocById.has(slot.id)) {
        await single(
          orpcClient.storageLocation.update([
            { id: slot.id, name: slot.name },
          ])
        );
      } else {
        const created = (await single(
          orpcClient.storageLocation.create([
            {
              warehouseAreaId: areaId,
              name: slot.name,
              enable: true,
            },
          ])
        )) as any;
        slotId = created.id;
      }

      syncedSlots.push({ ...slot, id: slotId });
    }

    syncedZones.push({ ...zone, id: areaId, slots: syncedSlots });
  }

  return { ...canvas, zones: syncedZones };
}

// Items live in productInventory, not the layout JSON, so drop them before
// persisting layoutConfig to keep productInventory the single source of truth.
export function stripLayoutItems(canvas: Warehouse): Warehouse {
  return {
    ...canvas,
    zones: canvas.zones.map((zone) => ({
      ...zone,
      slots: zone.slots.map((slot) => ({ ...slot, items: [] })),
    })),
  };
}
